import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatThreadDTO } from '../../global';
import type { ChatMessage } from '../../types';
import { dateLocale, t } from '../../lib/i18n';

/**
 * Sohbet ekranının durumu: sol menüdeki kayıtlı sohbetler, açık sohbet ve mesajları.
 *
 * Geçmiş sunucuda şifreli saklanır (docs/sohbet-gecmisi-plani.md, migration 0056) ve her
 * cihazda aynıdır. Gönderimde yalnızca yeni mesaj gider; geçmişi sunucu kurar. Konuşma
 * sunucuya kaydedilemediyse (eski sunucu, sınır, kayıt hatası) o konuşma bellekte sürer ve
 * sonraki mesajlar eski yolla (`chatWithAI`, geçmişi istemci verir) yanıtlanır.
 */

function stamp(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString(dateLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export interface ChatHistory {
  threads: ChatThreadDTO[];
  /** Açık sohbetin kimliği; yeni (henüz kaydedilmemiş) sohbette null. */
  threadId: string | null;
  messages: ChatMessage[];
  isResponding: boolean;
  /** Menüden bir sohbet açılırken mesajlar yükleniyor. */
  isOpening: boolean;
  send: (text: string) => Promise<void>;
  newChat: () => void;
  openThread: (id: string) => Promise<void>;
  renameThread: (id: string, title: string) => Promise<void>;
  deleteThread: (id: string) => Promise<void>;
  deleteAll: () => Promise<void>;
}

export function useChatHistory(enabled: boolean, onError: (message: string) => void): ChatHistory {
  const [threads, setThreads] = useState<ChatThreadDTO[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isResponding, setIsResponding] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  // Eşzamanlı işlemler (gönderirken sohbet değiştirme) güncel değeri ref'ten okur.
  const threadIdRef = useRef<string | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  /** Sohbet değiştikçe artar; gecikmiş bir yanıt başka bir sohbetin ekranına yazılmasın. */
  const viewRef = useRef(0);
  const errorRef = useRef(onError);
  errorRef.current = onError;

  const applyThreadId = useCallback((id: string | null) => {
    threadIdRef.current = id;
    setThreadId(id);
  }, []);
  const applyMessages = useCallback((update: (prev: ChatMessage[]) => ChatMessage[]) => {
    messagesRef.current = update(messagesRef.current);
    setMessages(messagesRef.current);
  }, []);

  const reload = useCallback(async () => {
    try {
      setThreads(await window.zabit.listChatThreads());
    } catch (err) {
      // Geçmiş menüsü açılmasa da sohbet çalışır; sessizce boş kalır.
      console.warn('[chat] sohbet listesi alınamadı:', (err as Error).message);
    }
  }, []);

  // Oturum açılınca liste çekilir; çıkışta (ya da hesap değişince) hepsi sıfırlanır.
  useEffect(() => {
    viewRef.current++;
    applyThreadId(null);
    applyMessages(() => []);
    setIsResponding(false);
    setThreads([]);
    if (enabled) void reload();
  }, [enabled, applyThreadId, applyMessages, reload]);

  const newChat = useCallback(() => {
    viewRef.current++;
    applyThreadId(null);
    applyMessages(() => []);
    setIsResponding(false);
    setIsOpening(false);
  }, [applyThreadId, applyMessages]);

  const touchThread = useCallback((id: string, title: string | null) => {
    const at = new Date().toISOString();
    setThreads((prev) => {
      const existing = prev.find((thread) => thread.id === id);
      const next: ChatThreadDTO = existing
        ? { ...existing, lastMessageAt: at, messageCount: existing.messageCount + 2 }
        : { id, title: title ?? t('Yeni sohbet', 'New chat'), createdAt: at, lastMessageAt: at, messageCount: 2 };
      return [next, ...prev.filter((thread) => thread.id !== id)];
    });
  }, []);

  const send = useCallback(
    async (text: string) => {
      const view = viewRef.current;
      const userMessage: ChatMessage = { id: `chat-${Date.now()}`, sender: 'user', text, timestamp: t('Şimdi', 'Now') };
      // Kayıtlı sohbet ya da yeni sohbet: yalnızca yeni mesaj gider. Kaydedilemeyen bir konuşma
      // (threadId yok ama mesaj var) eski yolla, geçmişle birlikte sürer.
      const stateless = threadIdRef.current === null && messagesRef.current.length > 0;
      const history = [...messagesRef.current, userMessage]
        .filter((message) => !message.isError)
        .map((message) => ({ role: (message.sender === 'user' ? 'user' : 'model') as 'user' | 'model', text: message.text }));

      applyMessages((prev) => [...prev, userMessage]);
      setIsResponding(true);

      try {
        let reply: string;
        if (stateless) {
          reply = await window.zabit.chatWithAI(history);
        } else {
          const result = await window.zabit.chatSend(threadIdRef.current, text);
          reply = result.reply;
          if (result.saved && result.threadId) {
            touchThread(result.threadId, result.title);
            if (view === viewRef.current) applyThreadId(result.threadId);
          }
        }
        if (view !== viewRef.current) return;
        applyMessages((prev) => [
          ...prev,
          { id: `chat-${Date.now() + 1}`, sender: 'assistant', text: reply || t('(boş yanıt döndü)', '(empty response)'), timestamp: t('Şimdi', 'Now') },
        ]);
      } catch (err) {
        if (view !== viewRef.current) return;
        applyMessages((prev) => [
          ...prev,
          { id: `chat-${Date.now() + 1}`, sender: 'assistant', text: `⚠️ ${(err as Error).message}`, timestamp: t('Şimdi', 'Now'), isError: true },
        ]);
      } finally {
        if (view === viewRef.current) setIsResponding(false);
      }
    },
    [applyMessages, applyThreadId, touchThread]
  );

  const openThread = useCallback(
    async (id: string) => {
      if (id === threadIdRef.current) return;
      const view = ++viewRef.current;
      applyThreadId(id);
      applyMessages(() => []);
      setIsResponding(false);
      setIsOpening(true);
      try {
        const detail = await window.zabit.getChatThread(id);
        if (view !== viewRef.current) return;
        applyMessages(() =>
          detail.messages.map((message) => ({
            id: message.id,
            sender: message.role === 'user' ? ('user' as const) : ('assistant' as const),
            text: message.text,
            timestamp: stamp(message.createdAt),
          }))
        );
      } catch (err) {
        if (view !== viewRef.current) return;
        errorRef.current((err as Error).message);
        applyThreadId(null);
        void reload();
      } finally {
        if (view === viewRef.current) setIsOpening(false);
      }
    },
    [applyMessages, applyThreadId, reload]
  );

  const renameThread = useCallback(async (id: string, title: string) => {
    try {
      const saved = await window.zabit.renameChatThread(id, title);
      setThreads((prev) => prev.map((thread) => (thread.id === id ? { ...thread, title: saved } : thread)));
    } catch (err) {
      errorRef.current((err as Error).message);
    }
  }, []);

  const deleteThread = useCallback(
    async (id: string) => {
      try {
        await window.zabit.deleteChatThread(id);
        setThreads((prev) => prev.filter((thread) => thread.id !== id));
        if (id === threadIdRef.current) newChat();
      } catch (err) {
        errorRef.current((err as Error).message);
      }
    },
    [newChat]
  );

  const deleteAll = useCallback(async () => {
    try {
      await window.zabit.deleteAllChatThreads();
      setThreads([]);
      newChat();
    } catch (err) {
      errorRef.current((err as Error).message);
    }
  }, [newChat]);

  return { threads, threadId, messages, isResponding, isOpening, send, newChat, openThread, renameThread, deleteThread, deleteAll };
}
